import PropTypes from 'prop-types';
import React from 'react';
import isEqual from 'react-fast-compare';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';

import {InaccessibleInfo} from 'auth/RequestAccess';
import * as SampleConstants from 'constants/SampleConstants';
import {AlwaysCollapsedTable} from 'notebook/tables/collapse/AlwaysCollapsedTable';
import {BenchlingDispatch} from 'redux-stores/Store';
import {bulkRemoveFulfillments, updateFulfillment} from 'requests/fulfillments/FulfillmentReduxActions';
import {HydratedFulfillmentWithPreparedSampleGroup} from 'requests/fulfillments/HydratedFulfillmentConstants';
import RequestFulfillmentsRetriever from 'requests/RequestFulfillmentsRetriever';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {REQUEST_TABLE_ICON, REQUEST_TABLE_TITLE} from './RequestConstants';
import RequestFulfillmentSpreadsheet from './RequestFulfillmentSpreadsheet';

export interface BaseRequestEntryTableProps {
  entryId: string;
  locked: boolean;
  viewer: boolean;
}
interface ReduxEnhancedProps {
  // Props provided by connect to redux
  isCollapsed: boolean;
  // Redux actions
  updateFulfillment: typeof updateFulfillment;
  bulkRemoveFulfillments: typeof bulkRemoveFulfillments;
}
export interface RequestEntryTableConnectorProps {
  fulfillmentIds: Array<string>;
  hydratedFulfillments: Array<HydratedFulfillmentWithPreparedSampleGroup | InaccessibleInfo>;
  containersById: {[containerId: string]: SampleConstants.Container | null};
  allContainerIds: Array<string>;
  ancestralStorablesById: {[storableId: string]: Array<SampleConstants.StorableSummary>} | null;
  areContainerLocationsLoading: boolean;
  areAnyFulfillmentsInaccessible: boolean;
}
type RequestEntryTableProps = BaseRequestEntryTableProps & ReduxEnhancedProps;

export class RequestEntryTable extends React.Component<RequestEntryTableProps> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static propTypes: any = {
    entryId: PropTypes.string.isRequired,
    locked: PropTypes.bool.isRequired,
    viewer: PropTypes.bool.isRequired,
  };

  override shouldComponentUpdate(nextProps: RequestEntryTableProps, nextState: {}): boolean {
    return !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);
  }

  override render(): JSX.Element | null {
    return (
      <RequestFulfillmentsRetriever
        entryId={this.props.entryId}
        locked={this.props.locked}
        viewer={this.props.viewer}
      >
        {(params: RequestEntryTableConnectorProps) => (
          <RequestEntryTableRenderer {...this.props} {...params} />
        )}
      </RequestFulfillmentsRetriever>
    );
  }
}

export function RequestEntryTableRenderer(
  props: RequestEntryTableProps & RequestEntryTableConnectorProps
): JSX.Element | null {
  const {fulfillmentIds, hydratedFulfillments, containersById, ancestralStorablesById} = props;

  if (fulfillmentIds.length === 0) {
    return null;
  }
  if (props.areContainerLocationsLoading) {
    return <CenteredHorizontalThrobber />;
  }
  if (props.areAnyFulfillmentsInaccessible) {
    return (
      <AlwaysCollapsedTable
        icon={REQUEST_TABLE_ICON}
        indentation={0}
        numRows={hydratedFulfillments.length}
        tableTypeLabel={REQUEST_TABLE_TITLE}
        tableName=""
        tooltipText="You do not have permission to view data for this request."
      />
    );
  }

  return (
    <RequestFulfillmentSpreadsheet
      locked={props.locked}
      entryId={props.entryId}
      hydratedFulfillments={hydratedFulfillments as Array<HydratedFulfillmentWithPreparedSampleGroup>}
      containersById={containersById}
      ancestralStorablesById={ancestralStorablesById!}
      viewer={props.viewer}
    />
  );
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const mapDispatchToProps = (dispatch: BenchlingDispatch) =>
  bindActionCreators({updateFulfillment, bulkRemoveFulfillments}, dispatch);

export default connect(null, mapDispatchToProps)(RequestEntryTable);
